import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AutocompleteInput from './AutocompleteInput';
import TagsInput from './TagsInput';
import useAutocomplete from '../hooks/useAutocomplete';
import useTags from '../hooks/useTags';
import { colors } from '../styles/styles';
import { useSwipeable } from 'react-swipeable';
import { fieldEditPageStyles } from './FieldEditPage.styles';
import { styles } from '../styles/styles';
import { getThoughtById } from '../services/thoughtService';

const FieldEditPage = ({ thoughts, onSave, updateThoughtLocally, themes = [], motifs = [], tags = [], isDataLoading = false }) => {
    const { id, fieldName } = useParams();
    const navigate = useNavigate();
    
    // Find the thought
    const [thought, setThought] = useState(null);
    // Tracks the id we've already resolved (props or by-id fetch) so the load
    // effect doesn't re-fire getThoughtById on every unrelated `thoughts` change.
    const loadedIdRef = useRef(null);

    // Add loading state to prevent flashing
    const [isLocalLoading, setIsLocalLoading] = useState(true);
    const isLoading = isDataLoading || isLocalLoading;
    
    // Handle navigation to detail page
    const handleCancel = () => {
        const container = document.querySelector('.field-edit-container');
        if (container) {
            container.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
            setTimeout(() => navigate(`/thought/${id}`), 180);
        } else {
            navigate(`/thought/${id}`);
        }
    };
    
    // Handle navigation to root page
    const handleBack = () => {
        const container = document.querySelector('.field-edit-container');
        if (container) {
            container.style.animation = 'fadeOut 0.2s cubic-bezier(0.2, 0, 0, 1) forwards';
            setTimeout(() => navigate('/', { replace: true }), 180);
        } else {
            navigate('/', { replace: true });
        }
    };
    
    // Swipe handlers for going back to root
    const swipeHandlers = useSwipeable({
        onSwipedRight: (eventData) => {
            if (eventData.initial[0] < 50) handleBack();
        },
        trackMouse: false,
        delta: 50,
        preventDefaultTouchmoveEvent: false,
    });
    
    // Load the thought on component mount.
    //
    // The home page loads only DRAFT thoughts, so editing a field on a triaged
    // thought (opened by URL) won't find it in `thoughts`. Fall back to fetching
    // by id before deciding it's missing — otherwise we'd bounce the user home.
    useEffect(() => {
        let cancelled = false;

        const foundThought = thoughts.find(t => t.id === id);
        if (foundThought) {
            loadedIdRef.current = id;
            setThought(foundThought);
            setTimeout(() => { if (!cancelled) setIsLocalLoading(false); }, 100);
            return () => { cancelled = true; };
        }

        // Not in the drafts array — fetch by id once before redirecting. The
        // loadedIdRef guard stops this from re-firing on every unrelated
        // `thoughts` change (e.g. a background drafts/vocab refresh) while the
        // field editor is open. Navigation to a new id still refetches.
        if (loadedIdRef.current !== id) {
            getThoughtById(id)
                .then(fetched => {
                    if (cancelled) return;
                    loadedIdRef.current = id;
                    if (fetched) {
                        setThought(fetched);
                        setIsLocalLoading(false);
                    } else {
                        setIsLocalLoading(false);
                        navigate('/'); // genuinely not found
                    }
                })
                .catch(() => {
                    if (cancelled) return;
                    loadedIdRef.current = id;
                    setIsLocalLoading(false);
                    navigate('/');
                });
        }

        return () => { cancelled = true; };
    }, [id, thoughts, navigate]);
    
    // State for the current field value
    const [importanceValue, setImportanceValue] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // State for comments field
    const [commentsValue, setCommentsValue] = useState('');
    
    // Initialize with available options from props
    const themeAutocomplete = useAutocomplete(themes);
    const motifAutocomplete = useAutocomplete(motifs);
    const tagsManager = useTags(tags);
    
    // Initialize values when thought is loaded
    useEffect(() => {
        if (thought) {
            // Set field-specific values
            if (fieldName === 'theme') {
                if (thought.theme) {
                    themeAutocomplete.selectOption(thought.theme);
                } else {
                    themeAutocomplete.resetInput();
                }
            } else if (fieldName === 'motif') {
                if (thought.motif) {
                    motifAutocomplete.selectOption(thought.motif);
                } else {
                    motifAutocomplete.resetInput();
                }
            } else if (fieldName === 'importance') {
                setImportanceValue(thought.importance !== undefined ? thought.importance : null);
            } else if (fieldName === 'tags') {
                if (thought.tags && thought.tags.length > 0) {
                    // Use setTags instead of resetTags + forEach
                    tagsManager.setTags(thought.tags);
                } else {
                    tagsManager.resetTags();
                }
            } else if (fieldName === 'comments') {
                setCommentsValue(thought.comments || '');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thought, fieldName]);

    // Update available options when they change
    useEffect(() => {
        themeAutocomplete.updateOptions(themes);
        motifAutocomplete.updateOptions(motifs);
        tagsManager.updateAvailableTags(tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themes, motifs, tags]);

    if (isLoading) {
        return (
            <div style={fieldEditPageStyles.container}>
                {/* Empty loading state without text */}
            </div>
        );
    }
    
    // Show error if thought wasn't found after loading
    if (!thought) {
        return (
            <div style={fieldEditPageStyles.container}>
                <p style={{ color: colors.textPrimary, marginBottom: styles.spacing.md }}>Item not found</p>
                <button 
                    onClick={() => navigate('/')} 
                    style={fieldEditPageStyles.saveButton(false)}
                >
                    Back to Library
                </button>
            </div>
        );
    }

    const handleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        
        try {
            const updatedThought = { ...thought };
            
            // Update only the specific field
            switch (fieldName) {
                case 'theme':
                    updatedThought.theme = themeAutocomplete.value;
                    break;
                case 'motif':
                    updatedThought.motif = motifAutocomplete.value;
                    break;
                case 'importance':
                    updatedThought.importance = importanceValue;
                    break;
                case 'tags':
                    updatedThought.tags = tagsManager.selectedTags;
                    break;
                case 'comments':
                    updatedThought.comments = commentsValue;
                    break;
                default:
                    break;
            }
            
            // If updateThoughtLocally is available, use it for optimistic updates
            if (updateThoughtLocally) {
                // Optimistically update the local state first
                updateThoughtLocally(updatedThought);
                
                // Save without triggering a full data reload
                await onSave(updatedThought, true); // Pass flag to avoid reload
            } else {
                // Fallback to regular save that might reload all data
                await onSave(updatedThought);
            }
            
            navigate(`/thought/${id}`);
        } catch (error) {
            console.error('Error saving field:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // Get field title for display
    const getFieldTitle = () => {
        switch (fieldName) {
            case 'theme':
                return 'Theme';
            case 'motif':
                return 'Motif';
            case 'importance':
                return 'Importance';
            case 'tags':
                return 'Tags';
            case 'comments':
                return 'Comments';
            default:
                return 'Edit Field';
        }
    };

    // Render the appropriate field editor based on fieldName
    const renderFieldEditor = () => {
        switch (fieldName) {
            case 'theme':
                return (
                    <AutocompleteInput
                        label="Theme"
                        value={themeAutocomplete.value}
                        onChange={themeAutocomplete.handleInputChange}
                        options={themeAutocomplete.filteredOptions}
                        onSelect={themeAutocomplete.selectOption}
                        onFocus={themeAutocomplete.showOptions}
                        onBlur={themeAutocomplete.hideOptions}
                        placeholder="Add a theme (e.g. Career, Relationships, Health)"
                        style={{ width: '100%', marginTop: '32px' }}
                    />
                );
            case 'motif':
                return (
                    <AutocompleteInput
                        label="Motif"
                        value={motifAutocomplete.value}
                        onChange={motifAutocomplete.handleInputChange}
                        options={motifAutocomplete.filteredOptions}
                        onSelect={motifAutocomplete.selectOption}
                        onFocus={motifAutocomplete.showOptions}
                        onBlur={motifAutocomplete.hideOptions}
                        placeholder="Add a recurring pattern or motif"
                        style={{ width: '100%', marginTop: '32px' }}
                    />
                );
            case 'tags':
                return (
                    <TagsInput
                        tagInput={tagsManager.tagInput}
                        selectedTags={tagsManager.selectedTags}
                        filteredTags={tagsManager.filteredTags}
                        showDropdown={tagsManager.showTagDropdown}
                        onInputChange={tagsManager.handleTagInputChange}
                        onKeyPress={tagsManager.handleTagKeyPress}
                        onFocus={() => tagsManager.setShowTagDropdown(true)}
                        onBlur={(e) => {
                            // Delay closing to check if click was on a dropdown item
                            // This allows clicking on dropdown items without closing
                            const currentTarget = e.currentTarget;
                            setTimeout(() => {
                                // Only close if focus is not within the input or dropdown
                                if (!currentTarget.contains(document.activeElement)) {
                                    tagsManager.setShowTagDropdown(false);
                                }
                            }, 300);
                        }}
                        onTagSelect={(tag) => {
                            tagsManager.addTag(tag);
                            // Keep dropdown open after selection
                            tagsManager.setShowTagDropdown(true);
                        }}
                        onTagRemove={tagsManager.removeTag}
                        style={{ width: '100%', marginTop: '32px' }}
                    />
                );
            case 'importance':
                return (
                    <div style={fieldEditPageStyles.fieldEditor.importance.container}>
                        <label style={fieldEditPageStyles.fieldEditor.importance.label}>
                            Importance
                        </label>
                        
                        <div style={fieldEditPageStyles.fieldEditor.importance.optionsContainer}>
                            <div style={fieldEditPageStyles.fieldEditor.importance.optionsList}>
                                <button
                                    type="button"
                                    onClick={() => setImportanceValue(null)}
                                    style={fieldEditPageStyles.fieldEditor.importance.optionButton(importanceValue === null)}
                                >
                                    <div style={fieldEditPageStyles.fieldEditor.importance.optionText(importanceValue === null)}>
                                        Not set
                                    </div>
                                    {importanceValue === null && (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 12L10 17L20 7" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </button>
                                
                                {[5, 4, 3, 2, 1].map(level => (
                                    <button
                                        key={level}
                                        type="button"
                                        onClick={() => setImportanceValue(level)}
                                        style={fieldEditPageStyles.fieldEditor.importance.optionButton(importanceValue === level)}
                                    >
                                        <div style={fieldEditPageStyles.fieldEditor.importance.levelIndicator}>
                                            {level}
                                        </div>
                                        <div style={fieldEditPageStyles.fieldEditor.importance.optionText(importanceValue === level)}>
                                            {level === 5 ? 'Very important' : 
                                             level === 4 ? 'Important' : 
                                             level === 3 ? 'Moderate' : 
                                             level === 2 ? 'Low importance' : 
                                             'Very low importance'}
                                        </div>
                                        {importanceValue === level && (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5 12L10 17L20 7" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'comments':
                return (
                    <div style={fieldEditPageStyles.fieldEditor.comments.container}>
                        <div style={fieldEditPageStyles.fieldEditor.comments.header}>
                            <button 
                                onClick={handleCancel}
                                style={fieldEditPageStyles.backButton}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 18L9 12L15 6" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            
                            <h1 style={fieldEditPageStyles.title}>
                                Comments
                            </h1>
                            
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={fieldEditPageStyles.saveButton(isSaving)}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                        
                        <div style={fieldEditPageStyles.fieldEditor.comments.content}>
                            {thought && (
                                <div style={fieldEditPageStyles.fieldEditor.comments.thoughtDisplay}>
                                    <div style={fieldEditPageStyles.fieldEditor.comments.thoughtText}>
                                        {thought.thought || thought.content || ''}
                                    </div>
                                </div>
                            )}
                            
                            <div style={fieldEditPageStyles.fieldEditor.comments.textareaContainer}>
                                <textarea
                                    value={commentsValue}
                                    onChange={(e) => setCommentsValue(e.target.value)}
                                    placeholder="Add your comments here..."
                                    style={fieldEditPageStyles.fieldEditor.comments.textarea}
                                    autoFocus
                                />
                            </div>
                        </div>
                    </div>
                );
            default:
                return <div>Invalid field</div>;
        }
    };

    return (
        <div 
            {...swipeHandlers}
            className="field-edit-container"
            style={fieldEditPageStyles.container}
        >
            <div style={fieldEditPageStyles.header}>
                <button 
                    onClick={handleCancel}
                    style={fieldEditPageStyles.backButton}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                
                <h1 style={fieldEditPageStyles.title}>
                    {getFieldTitle()}
                </h1>
                
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={fieldEditPageStyles.saveButton(isSaving)}
                >
                    {isSaving && (
                        <span style={fieldEditPageStyles.savingSpinner} />
                    )}
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </div>
            
            <div style={fieldEditPageStyles.content}>
                {renderFieldEditor()}
                
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default FieldEditPage; 